import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { MarketingHeader, MarketingFooter, LandingStyles } from './OtherPages';
import {
  ArrowRight, Play, Sparkles, Target, FileText, Check, CheckCircle2, X, Star,
  ChevronLeft, ChevronRight, TrendingUp, Zap, BarChart3, Layers,
  Upload, ScanLine, UserCheck, Flag, Percent, Timer, FileSearch, Trophy, Eye,
  Building2, Calendar, RotateCcw, Loader2, Columns,
} from 'lucide-react';

// ── Data ───────────────────────────────────────────────────────────────────────
const PIPELINE_STAGES = [
  { Icon: Upload, title: 'Application submitted', tag: 'Your file lands in the system instantly.', body: 'You apply via a job board or company portal. The ATS captures your file and begins processing it before any human sees it.' },
  { Icon: FileSearch, title: 'Parsing & extraction', tag: 'Broken into structured data fields.', body: 'The system extracts name, contact, work history, education, and skills. Unusual layouts, tables, and images can scramble this step.' },
  { Icon: Target, title: 'Keyword scoring', tag: 'Matched against the job description.', body: 'Every required skill, tool, or qualification that matches the posting earns a score point. Missing terms lower your rank.' },
  { Icon: BarChart3, title: 'Ranking & filtering', tag: 'Only the top 20-30% survive.', body: 'Candidates are automatically ranked by score. Resumes below a threshold are filtered out before any recruiter opens them.' },
  { Icon: Eye, title: 'Human review', tag: 'A recruiter finally sees your resume.', body: 'Top-ranked resumes reach a recruiter — only candidates who passed the keyword and format test are visible at this point.' },
  { Icon: Flag, title: 'Interview invitation', tag: 'Top candidates get contacted.', body: 'The recruiter shortlists and contacts selected candidates. Surviving the first five steps is what gets you here.' },
];

const SCAN_STEPS = ['Extracting text', 'Checking formatting', 'Matching keywords', 'Calculating score'];
const SCAN_KEYWORDS = [
  { label: 'Project Management', matched: true }, { label: 'Agile', matched: true },
  { label: 'Stakeholder Communication', matched: true }, { label: 'Budget Forecasting', matched: false },
];
const SCAN_FORMAT_CHECKS = ['Single-column layout', 'Standard section headings', 'Text-based skills section', 'Consistent date formatting'];

const JOB_KEYWORDS = ['React', 'TypeScript', 'Agile', 'Leadership', 'Testing'];
const JOB_DESC_KW_PARTS = [
  'Seeking a Senior Engineer with ', { kw: 'React' }, ' and ', { kw: 'TypeScript' }, ' expertise. Comfortable with ',
  { kw: 'Agile' }, ' teams, providing technical ', { kw: 'Leadership' }, ', and writing thorough ', { kw: 'Testing' }, ' coverage.',
];
const RESUME_KW_PARTS = [
  'Senior Software Engineer with 7+ years building production ', { kw: 'React' }, ' and ', { kw: 'TypeScript' },
  ' applications in ', { kw: 'Agile' }, ' teams. Provided technical ', { kw: 'Leadership' }, ' and championed ',
  { kw: 'Testing' }, ' best practices across a 12-engineer org.',
];

const DO_ITEMS = [
  { title: 'Mirror exact keywords', snippet: 'Skills: Project Management, Agile, Scrum', body: 'Copy the exact phrasing from the posting — if it says "project management", use that term, not "managing projects".' },
  { title: 'Use standard headings', snippet: 'Work Experience · Education · Skills', body: 'Stick to headings ATS systems recognize reliably: Work Experience, Education, Skills, Certifications.' },
  { title: 'Submit as PDF', snippet: 'resume_sarah_chen.pdf', body: 'PDF is safe for most modern ATS systems and preserves formatting reliably across parsers.' },
  { title: 'Keep dates consistent', snippet: 'Jan 2022 – Mar 2024', body: 'Use a single format throughout. Mixing "01/2022" and "Jan 2022" confuses parsers.' },
];
const DONT_ITEMS = [
  { title: 'Tables for layout', snippet: '[ Skills table | Experience table ]', body: 'Text inside table cells is often skipped entirely. Use single-column blocks instead.' },
  { title: 'Contact in header', snippet: 'Header: name@email.com', body: 'Many ATS systems skip header and footer regions — put contact info in the main body.' },
  { title: 'Icon-based skill bars', snippet: '★★★★☆ Excel', body: 'Rating bars and graphics are invisible to parsers. Write skill levels as plain text.' },
  { title: 'White-text keyword stuffing', snippet: 'React React React React', body: 'Hiding repeated keywords in white text is instantly penalized and can disqualify you entirely.' },
];

const CHECKLIST_ITEMS = [
  'Resume saved as PDF',
  'File named: FirstName-LastName-JobTitle.pdf',
  'Contact details in the main body — not a header',
  'Job title mirrors the posting’s headline',
  'Every required skill listed in a Skills section',
  'Top 5 keywords from the job description appear in bullets',
  'No tables, text boxes, or columns holding key content',
  'No graphics or icon-based skill ratings',
  'Dates are consistent throughout',
  'ATS Checker score is 70%+ before submitting',
];

const MYTHS = [
  { myth: 'A creative, colorful resume stands out more.', fact: 'Bold colors and graphics often get skipped entirely by ATS parsers — clean formatting wins.' },
  { myth: 'Keyword stuffing guarantees a higher score.', fact: 'Modern ATS systems detect stuffing and can penalize resumes that repeat keywords unnaturally.' },
  { myth: 'If it looks good to me, it will pass any ATS.', fact: 'ATS software reads raw text, not visual design — what looks great to a human can be unreadable to a parser.' },
  { myth: 'PDFs never work with ATS.', fact: 'Most modern ATS platforms parse PDFs perfectly, as long as the layout uses real text, not images.' },
  { myth: 'One resume works for every application.', fact: 'ATS scores are calculated against each specific job description — tailoring is essential.' },
  { myth: 'Only large companies use ATS software.', fact: 'Over 70% of mid-sized companies use an ATS too, not just Fortune 500s.' },
];

const STATS = [
  { Icon: Percent, value: 75, suffix: '%', label: 'of resumes rejected by ATS before recruiter review' },
  { Icon: Building2, value: 98, suffix: '%', label: 'of Fortune 500 companies use ATS software' },
  { Icon: Timer, value: 6, suffix: ' sec', label: 'average recruiter time on a resume that passes' },
  { Icon: TrendingUp, value: 3, suffix: '×', label: 'more interviews for ATS-optimized resumes' },
];

const FAQS = [
  ['Does every company use an ATS?', 'Around 98% of Fortune 500 companies use an ATS, and over 70% of mid-sized companies use one too. The exceptions are very small businesses that receive only a handful of applications per role.'],
  ['Will a designed template hurt my ATS score?', 'It depends on the template. Two-column designs with proper text flow — like most MarsResume templates — are fine. The risk comes from tables used to create columns, or key content inside floating text boxes and headers.'],
  ['Should I create a separate "ATS version" of my resume?', 'Only if the role specifically calls for a plain-text submission. A clean designed template that uses real text (not images) will usually parse correctly and still look great to a recruiter.'],
  ['How long should my resume be for ATS?', 'Length itself doesn’t affect ATS scoring, but keeping it focused to 1-2 pages ensures every keyword you include is relevant and strong.'],
  ['Can I use the same resume for every job?', 'No. ATS systems compare your resume to the specific job description. Use MarsResume’s Tailor tool to customize each application in minutes.'],
  ['Does the order of sections matter?', 'Yes. Most ATS systems weight content near the top more heavily. Put your summary and most relevant experience first.'],
  ['What score is "good" on an ATS?', 'Scores above 70-75% match generally clear most automated filters. Our ATS Checker shows your score and specific missing keywords.'],
  ['Do ATS systems read LinkedIn URLs and portfolio links?', 'Most parsers extract hyperlinks but don’t visit them. Always include the full URL in plain text so it can be parsed even if the link isn’t clickable.'],
];

// ── Small shared pieces ───────────────────────────────────────────────────────
const SectionHeading = ({ title, subtitle }) => (
  <div className="atsg-sec-heading">
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

const InlineCTA = ({ navigate, label = 'Check your resume’s ATS score' }) => (
  <div className="atsg-inline-cta-wrap">
    <button type="button" className="atsg-inline-cta" onClick={() => navigate('/ats-checker')}>{label} <ArrowRight size={13} /></button>
  </div>
);

const AnimatedCounter = ({ value, decimals = 0, duration = 1600, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(value * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(value);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

const ScoreRing = ({ score, size = 140, stroke = 12, color = '#059669', trackColor = '#D1FAE5', duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(score * eased);
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(score);
    };
    requestAnimationFrame(step);
  }, [score, duration]);

  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - display / 100);

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.1s linear' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: size * 0.24, fontWeight: 900, color: '#0F172A', letterSpacing: -1, lineHeight: 1 }}>{Math.round(display)}%</div>
      </div>
    </div>
  );
};

// ── Hero ───────────────────────────────────────────────────────────────────────
const DASH_ROWS = [
  { Icon: FileText, label: 'Resume Score', sub: 'Overall quality', value: '91%', color: '#059669', bg: '#ECFDF5' },
  { Icon: Target, label: 'Keyword Match', sub: '18 of 20 matched', value: '90%', color: '#1D4ED8', bg: '#EFF6FF' },
  { Icon: Columns, label: 'Formatting Check', sub: 'ATS-safe layout', value: 'Pass', color: '#7C3AED', bg: '#F5F3FF' },
  { Icon: UserCheck, label: 'Recruiter Readiness', sub: 'Above threshold', value: 'Ready', color: '#EF1D28', bg: '#FFF1F2' },
];
const PARTICLES = Array.from({ length: 10 }).map((_, i) => ({
  left: `${8 + (i * 9) % 90}%`, top: `${10 + (i * 23) % 80}%`, delay: (i % 5) * 1.2, duration: 6 + (i % 4) * 1.5,
}));

const HeroSection = ({ navigate, onWatchDemo }) => {
  const [scoreFill, setScoreFill] = useState(0);
  useEffect(() => { const t = setTimeout(() => setScoreFill(91), 400); return () => clearTimeout(t); }, []);

  return (
    <section className="atsg-hero">
      <div className="atsg-hero-grid-bg" />
      <div className="atsg-hero-glow" />
      {PARTICLES.map((p, i) => (
        <span key={i} className="atsg-particle" style={{ left: p.left, top: p.top, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }} />
      ))}
      <div className="atsg-hero-inner">
        <div className="atsg-hero-left">
          <h1>Why your resume<br />never reaches a human.</h1>
          <p>75% of resumes are rejected by ATS software before a recruiter ever sees them. See exactly how these systems work — and how to pass every one.</p>
          <div className="atsg-hero-ctas">
            <button type="button" className="atsg-btn-primary" onClick={() => navigate('/ats-checker')}><Sparkles size={16} /> Check My Resume</button>
            <button type="button" className="atsg-btn-ghost-dark" onClick={onWatchDemo}><Play size={15} /> Watch ATS Demo</button>
          </div>
        </div>
        <div className="atsg-hero-right">
          <motion.div className="atsg-dash-card" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="atsg-dash-chrome">
              <span /><span /><span />
              <div className="atsg-dash-url"><span>🔒</span> marsresume.com/ats-checker</div>
            </div>
            <div className="atsg-dash-body">
              {DASH_ROWS.map(row => (
                <div key={row.label} className="atsg-dash-row">
                  <div className="atsg-dash-row-icon" style={{ background: row.bg, color: row.color }}><row.Icon size={16} /></div>
                  <div className="atsg-dash-row-text">
                    <div className="atsg-dash-row-label">{row.label}</div>
                    <div className="atsg-dash-row-sub">{row.sub}</div>
                    {row.label === 'Resume Score' && (
                      <div className="atsg-dash-row-bar"><motion.div className="atsg-dash-row-bar-fill" animate={{ width: `${scoreFill}%` }} transition={{ duration: 1.2, ease: 'easeOut' }} /></div>
                    )}
                  </div>
                  <div className="atsg-dash-row-value" style={{ color: row.color, background: row.bg }}>{row.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div className="atsg-float-badge atsg-float-badge-1" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
            <span className="atsg-float-icon atsg-float-icon-green"><CheckCircle2 size={15} /></span>
            <span>ATS-Safe Format</span>
          </motion.div>
          <motion.div className="atsg-float-badge atsg-float-badge-2" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }}>
            <span className="atsg-float-icon atsg-float-icon-red"><Target size={15} /></span>
            <span>18/20 Keywords</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ── Recruiter pipeline ────────────────────────────────────────────────────────
const PipelineSection = ({ navigate }) => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 900);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const track = (
    <motion.div className="atsg-pipe-track" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}>
      {PIPELINE_STAGES.map((stage, i) => (
        <React.Fragment key={stage.title}>
          <motion.div className="atsg-pipe-card" variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -8 }}>
            <div className="atsg-pipe-num">{String(i + 1).padStart(2, '0')}</div>
            <div className="atsg-pipe-icon"><stage.Icon size={20} /></div>
            <div className="atsg-pipe-title">{stage.title}</div>
            <p className="atsg-pipe-tag">{stage.tag}</p>
            <div className="atsg-pipe-body">{stage.body}</div>
          </motion.div>
          {i < PIPELINE_STAGES.length - 1 && (
            <div className="atsg-pipe-arrow">
              <motion.div className="atsg-pipe-arrow-line" style={{ transformOrigin: isMobile ? 'top' : 'left' }}
                initial={isMobile ? { scaleY: 0 } : { scaleX: 0 }} whileInView={isMobile ? { scaleY: 1 } : { scaleX: 1 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.15 + i * 0.06 }} />
              <ArrowRight size={14} className="atsg-pipe-arrow-icon" />
            </div>
          )}
        </React.Fragment>
      ))}
    </motion.div>
  );

  return (
    <section className="atsg-section">
      <SectionHeading title="The 6-step journey of your resume" subtitle="Every application goes through this pipeline. Most resumes are eliminated in steps 3 or 4. Hover a card for detail." />
      {isMobile ? track : <div className="atsg-pipe-scroll">{track}</div>}
      <InlineCTA navigate={navigate} />
    </section>
  );
};

// ── ATS Scanner demo ──────────────────────────────────────────────────────────
const ScannerDemo = ({ navigate }) => {
  const [status, setStatus] = useState('idle');
  const [stepIdx, setStepIdx] = useState(-1);
  const timersRef = useRef([]);

  const runScan = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setStatus('scanning');
    setStepIdx(-1);
    SCAN_STEPS.forEach((_, i) => {
      const t = setTimeout(() => setStepIdx(i), 550 * (i + 1));
      timersRef.current.push(t);
    });
    const finalT = setTimeout(() => setStatus('done'), 550 * (SCAN_STEPS.length + 1));
    timersRef.current.push(finalT);
  };

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  return (
    <section className="atsg-section atsg-section-tint" id="atsg-scanner">
      <SectionHeading title="See an ATS scan happen live" subtitle="Run a sample resume through the same checks a real ATS performs." />
      <div className="atsg-scanner-card">
        {status !== 'done' && (
          <div className="atsg-scanner-drop">
            <div className="atsg-scanner-drop-icon"><ScanLine size={26} /></div>
            <div className="atsg-scanner-drop-title">{status === 'idle' ? 'Sample resume ready to scan' : 'Scanning sample_resume.pdf…'}</div>
            <button type="button" className="atsg-btn-primary" onClick={runScan} disabled={status === 'scanning'}>
              {status === 'scanning' ? <><Loader2 size={16} className="atsg-spin" /> Scanning…</> : <><Upload size={16} /> Scan Sample Resume</>}
            </button>
            {status === 'scanning' && (
              <div className="atsg-scanner-steps">
                {SCAN_STEPS.map((s, i) => (
                  <div key={s} className={`atsg-scanner-step${i <= stepIdx ? ' done' : ''}`}>
                    {i <= stepIdx ? <CheckCircle2 size={14} /> : <span className="atsg-scanner-step-dot" />} {s}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <AnimatePresence>
          {status === 'done' && (
            <motion.div className="atsg-scanner-result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="atsg-scanner-result-top">
                <ScoreRing score={88} />
                <div>
                  <div className="atsg-scanner-result-title">Strong ATS Score</div>
                  <p>This resume is well-structured and passes formatting checks.</p>
                  <button type="button" className="atsg-btn-secondary" onClick={runScan}><RotateCcw size={14} /> Scan Again</button>
                </div>
              </div>
              <div className="atsg-scanner-result-grid">
                <div>
                  <div className="atsg-scanner-result-label">Keyword matches</div>
                  <div className="atsg-scanner-chips">
                    {SCAN_KEYWORDS.map(k => (
                      <span key={k.label} className={`atsg-scanner-chip${k.matched ? ' matched' : ''}`}>
                        {k.matched ? <Check size={11} /> : <X size={11} />} {k.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="atsg-scanner-result-label">Formatting checks</div>
                  <div className="atsg-scanner-checks">
                    {SCAN_FORMAT_CHECKS.map(c => <div key={c} className="atsg-scanner-check"><Check size={13} /> {c}</div>)}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <InlineCTA navigate={navigate} label="Scan your real resume now" />
    </section>
  );
};

// ── Keyword matching demo ─────────────────────────────────────────────────────
const KeywordText = ({ parts, matchedSet }) => (
  <p>
    {parts.map((p, i) => typeof p === 'string' ? <span key={i}>{p}</span> : (
      <span key={i} className={`atsg-kw${matchedSet.has(p.kw) ? ' active' : ''}`}>{p.kw}</span>
    ))}
  </p>
);

const KeywordMatchSection = ({ navigate }) => {
  const [matchedCount, setMatchedCount] = useState(0);
  const [running, setRunning] = useState(false);
  const timersRef = useRef([]);
  const matchedSet = useMemo(() => new Set(JOB_KEYWORDS.slice(0, matchedCount)), [matchedCount]);
  const score = Math.round(62 + (matchedCount / JOB_KEYWORDS.length) * 29);

  const run = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setRunning(true);
    setMatchedCount(0);
    JOB_KEYWORDS.forEach((_, i) => {
      const t = setTimeout(() => setMatchedCount(i + 1), 500 * (i + 1));
      timersRef.current.push(t);
    });
    const doneT = setTimeout(() => setRunning(false), 500 * (JOB_KEYWORDS.length + 1));
    timersRef.current.push(doneT);
  };
  const reset = () => { timersRef.current.forEach(clearTimeout); setMatchedCount(0); setRunning(false); };

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  return (
    <section className="atsg-section">
      <SectionHeading title="Watch keywords match in real time" subtitle="Run the match and see your ATS score climb as each keyword lines up." />
      <div className="atsg-tailor-card">
        <div className="atsg-tailor-grid">
          <div className="atsg-tailor-col">
            <div className="atsg-tailor-col-head"><Building2 size={14} /> Job Description</div>
            <div className="atsg-tailor-text"><KeywordText parts={JOB_DESC_KW_PARTS} matchedSet={matchedSet} /></div>
          </div>
          <div className="atsg-tailor-col">
            <div className="atsg-tailor-col-head"><FileText size={14} /> Your Resume</div>
            <div className="atsg-tailor-text"><KeywordText parts={RESUME_KW_PARTS} matchedSet={matchedSet} /></div>
          </div>
        </div>
        <div className="atsg-tailor-footer">
          <div className="atsg-tailor-score">
            <span>ATS Match Score</span>
            <div className="atsg-tailor-score-row">
              <span className="atsg-tailor-score-value" style={{ color: matchedCount === JOB_KEYWORDS.length ? '#059669' : '#D97706' }}>{score}%</span>
              <span className="atsg-tailor-score-sub">{matchedCount}/{JOB_KEYWORDS.length} keywords matched</span>
            </div>
          </div>
          <button type="button" className="atsg-btn-primary" onClick={matchedCount > 0 ? reset : run} disabled={running}>
            {matchedCount > 0 && !running ? <><RotateCcw size={16} /> Reset</> : running ? 'Matching…' : <><Zap size={16} /> Run Keyword Match</>}
          </button>
        </div>
      </div>
      <InlineCTA navigate={navigate} />
    </section>
  );
};

// ── Do vs Don't ───────────────────────────────────────────────────────────────
const DoDontCard = ({ item, positive }) => (
  <motion.div className={`atsg-dd-card${positive ? ' positive' : ' negative'}`} whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 300, damping: 24 }}>
    <div className="atsg-dd-head">
      <span className="atsg-dd-icon">{positive ? <Check size={13} /> : <X size={13} />}</span>
      {item.title}
    </div>
    <div className="atsg-dd-snippet">{item.snippet}</div>
    <div className="atsg-dd-explain">{item.body}</div>
  </motion.div>
);

const DoDontSection = ({ navigate }) => (
  <section className="atsg-section atsg-section-tint">
    <SectionHeading title="What ATS rewards vs. penalizes" subtitle="Real resume snippets. Hover each card for the full explanation." />
    <div className="atsg-dd-grid">
      <div className="atsg-dd-col">
        <div className="atsg-dd-col-head positive"><CheckCircle2 size={16} /> Do this</div>
        {DO_ITEMS.map(item => <DoDontCard key={item.title} item={item} positive />)}
      </div>
      <div className="atsg-dd-col">
        <div className="atsg-dd-col-head negative"><X size={16} /> Avoid this</div>
        {DONT_ITEMS.map(item => <DoDontCard key={item.title} item={item} />)}
      </div>
    </div>
    <InlineCTA navigate={navigate} />
  </section>
);

// ── Before vs After ───────────────────────────────────────────────────────────
const BeforeAfterSection = ({ navigate }) => (
  <section className="atsg-section">
    <SectionHeading title="Before vs. after an ATS pass" subtitle="Same experience. One version a parser can actually read." />
    <div className="atsg-ba-card">
      <div className="atsg-ba-grid">
        <div className="atsg-ba-resume before">
          <div className="atsg-ba-resume-label">Before</div>
          <div className="atsg-ba-mock">
            <div className="atsg-ba-mock-cols">
              <div className="atsg-ba-mock-col"><span /><span /><span className="short" /></div>
              <div className="atsg-ba-mock-col"><span /><span /><span className="short" /></div>
            </div>
            <div className="atsg-ba-mock-flag"><X size={11} /> 2-column table layout</div>
            <div className="atsg-ba-mock-flag"><X size={11} /> Icon-based skill bars</div>
          </div>
          <motion.div className="atsg-ba-score-bar" initial={{ width: 0 }} whileInView={{ width: '41%' }} viewport={{ once: true }} transition={{ duration: 1, ease: 'easeOut' }} style={{ background: '#EF1D28' }} />
          <div className="atsg-ba-score-label" style={{ color: '#EF1D28' }}>41% ATS Score</div>
        </div>
        <div className="atsg-ba-resume after">
          <div className="atsg-ba-resume-label">After</div>
          <div className="atsg-ba-mock">
            <div className="atsg-ba-mock-single"><span /><span /><span /><span className="short" /></div>
            <div className="atsg-ba-mock-flag good"><Check size={11} /> Single-column layout</div>
            <div className="atsg-ba-mock-flag good"><Check size={11} /> Text-based skills section</div>
          </div>
          <motion.div className="atsg-ba-score-bar" initial={{ width: 0 }} whileInView={{ width: '89%' }} viewport={{ once: true }} transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }} style={{ background: '#059669' }} />
          <div className="atsg-ba-score-label" style={{ color: '#059669' }}>89% ATS Score</div>
        </div>
      </div>
    </div>
    <InlineCTA navigate={navigate} />
  </section>
);

// ── Interactive checklist ─────────────────────────────────────────────────────
const ChecklistSection = ({ navigate }) => {
  const [checked, setChecked] = useState(() => Array(CHECKLIST_ITEMS.length).fill(false));
  const checkedCount = checked.filter(Boolean).length;
  const pct = Math.round((checkedCount / CHECKLIST_ITEMS.length) * 100);
  const complete = checkedCount === CHECKLIST_ITEMS.length;

  const toggle = (i) => setChecked(prev => prev.map((v, idx) => idx === i ? !v : v));

  return (
    <section className="atsg-section atsg-section-tint">
      <SectionHeading title="Before you hit submit" subtitle="Check off every item. This alone can double your response rate." />
      <div className="atsg-checklist-card">
        <div className="atsg-checklist-progress-row">
          <div className="atsg-checklist-progress-track"><motion.div className="atsg-checklist-progress-fill" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} /></div>
          <span className="atsg-checklist-progress-label">{checkedCount}/{CHECKLIST_ITEMS.length}</span>
        </div>
        <AnimatePresence>
          {complete && (
            <motion.div className="atsg-checklist-badge" initial={{ opacity: 0, y: -10, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}>
              <Trophy size={16} /> ATS-Ready! All checks complete.
            </motion.div>
          )}
        </AnimatePresence>
        <div className="atsg-checklist-items">
          {CHECKLIST_ITEMS.map((item, i) => (
            <button type="button" key={item} className={`atsg-checklist-item${checked[i] ? ' checked' : ''}`} onClick={() => toggle(i)}>
              <span className="atsg-checklist-box">{checked[i] && <Check size={13} strokeWidth={3} />}</span>
              {item}
            </button>
          ))}
        </div>
      </div>
      <InlineCTA navigate={navigate} label="Verify with the real ATS Checker" />
    </section>
  );
};

// ── Myths vs Facts ─────────────────────────────────────────────────────────────
const MythsSection = ({ navigate }) => (
  <section className="atsg-section">
    <SectionHeading title="ATS myths vs. facts" subtitle="Hover a card to flip it." />
    <div className="atsg-myths-grid">
      {MYTHS.map(m => (
        <div key={m.myth} className="atsg-flip">
          <div className="atsg-flip-inner">
            <div className="atsg-flip-face atsg-flip-front">
              <div className="atsg-flip-label">Myth</div>
              <p>{m.myth}</p>
            </div>
            <div className="atsg-flip-face atsg-flip-back">
              <div className="atsg-flip-label">Reality</div>
              <p>{m.fact}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
    <InlineCTA navigate={navigate} />
  </section>
);

// ── Statistics ─────────────────────────────────────────────────────────────────
const StatsSection = () => (
  <section className="atsg-section">
    <motion.div className="atsg-stats-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}>
      {STATS.map(stat => (
        <motion.div key={stat.label} className="atsg-stat-card" variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.4 }} whileHover={{ y: -4 }}>
          <div className="atsg-stat-icon"><stat.Icon size={20} /></div>
          <div className="atsg-stat-value"><AnimatedCounter value={stat.value} suffix={stat.suffix} /></div>
          <div className="atsg-stat-label">{stat.label}</div>
        </motion.div>
      ))}
    </motion.div>
  </section>
);

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="atsg-section">
      <SectionHeading title="Common questions answered" />
      <div className="atsg-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`atsg-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="atsg-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="atsg-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="atsg-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Final CTA ──────────────────────────────────────────────────────────────────
const FinalCTASection = ({ navigate }) => (
  <section className="atsg-cta-outer">
    <motion.div className="atsg-cta-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.5 }}>
      <div className="atsg-cta-mesh" />
      <div className="atsg-cta-glow" />
      <div className="atsg-cta-content">
        <div className="atsg-cta-left">
          <h2>Ready to pass every ATS?</h2>
          <p>Check your real resume in seconds — free, no signup required to see your score.</p>
          <button type="button" className="atsg-btn-primary" onClick={() => navigate('/ats-checker')}><Sparkles size={16} /> Check My Resume</button>
        </div>
        <div className="atsg-cta-right">
          <motion.div className="atsg-cta-dash" animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
            <div className="atsg-cta-dash-chrome"><span /><span /><span /></div>
            <div className="atsg-cta-dash-body">
              {DASH_ROWS.slice(0, 3).map(row => (
                <div key={row.label} className="atsg-cta-dash-row">
                  <div className="atsg-cta-dash-row-icon" style={{ background: row.bg, color: row.color }}><row.Icon size={14} /></div>
                  <div className="atsg-cta-dash-row-label">{row.label}</div>
                  <div className="atsg-cta-dash-row-value" style={{ color: row.color }}>{row.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  </section>
);

// ── Main page ────────────────────────────────────────────────────────────────
export const AtsGuidePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  usePageSEO({
    title: 'ATS Guide – How to Beat Applicant Tracking Systems | MarsResume',
    description: 'A complete interactive guide to ATS systems — how they work, why resumes get rejected, and every step to optimize your resume to pass ATS screening and reach human recruiters.',
    canonical: '/ats-guide',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const scannerRef = useRef(null);
  const scrollToScanner = () => scannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="atsg-page">
      <LandingStyles />
      <AtsGuideStyles />
      <MarketingHeader />

      <HeroSection navigate={navigate} onWatchDemo={scrollToScanner} />
      <PipelineSection navigate={navigate} />
      <div ref={scannerRef}><ScannerDemo navigate={navigate} /></div>
      <KeywordMatchSection navigate={navigate} />
      <DoDontSection navigate={navigate} />
      <BeforeAfterSection navigate={navigate} />
      <ChecklistSection navigate={navigate} />
      <MythsSection navigate={navigate} />
      <StatsSection />
      <FAQSection />
      <FinalCTASection navigate={navigate} />

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const AtsGuideStyles = () => (
  <style>{`
    .atsg-page { min-height: 100vh; background: #F9FAFB; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    @keyframes atsgHeroFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
    @keyframes atsgParticle { 0%{transform:translate(0,0); opacity:0;} 12%{opacity:0.8;} 88%{opacity:0.6;} 100%{transform:translate(14px,-110px); opacity:0;} }
    @keyframes atsgSpin { to { transform: rotate(360deg); } }
    .atsg-spin { animation: atsgSpin 0.8s linear infinite; }

    /* Buttons */
    .atsg-btn-primary { background: #EF1D28; color: white; border: none; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; box-shadow: 0 6px 20px rgba(239,29,40,0.3); }
    .atsg-btn-primary:hover:not(:disabled) { background: #D91620; transform: translateY(-2px); box-shadow: 0 10px 28px rgba(239,29,40,0.4); }
    .atsg-btn-primary:disabled { opacity: 0.75; cursor: default; }
    .atsg-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
    .atsg-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); transform: translateY(-2px); }
    .atsg-btn-secondary { background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 10px; padding: 10px 18px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 7px; transition: all 0.2s; margin-top: 12px; }
    .atsg-btn-secondary:hover { border-color: #CBD5E1; background: #F9FAFB; }

    /* Section heading */
    .atsg-section { padding: 76px 48px; max-width: 1180px; margin: 0 auto; }
    .atsg-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .atsg-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .atsg-sec-heading { text-align: center; margin-bottom: 48px; }
    .atsg-sec-heading h2 { margin: 0; font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .atsg-sec-heading p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 580px; }

    /* Inline CTA */
    .atsg-inline-cta-wrap { text-align: center; margin-top: 40px; }
    .atsg-inline-cta { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; color: #EF1D28; font-size: 13.5px; font-weight: 700; cursor: pointer; font-family: inherit; padding: 6px 10px; border-radius: 8px; transition: background 0.15s; }
    .atsg-inline-cta:hover { background: #FFF1F2; }

    /* Hero */
    .atsg-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 88px 48px 96px; position: relative; overflow: hidden; }
    .atsg-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .atsg-hero-glow { position: absolute; top: 10%; left: 15%; width: 700px; height: 420px; background: radial-gradient(ellipse, rgba(239,29,40,0.14) 0%, transparent 70%); pointer-events: none; }
    .atsg-particle { position: absolute; width: 4px; height: 4px; border-radius: 50%; background: rgba(239,29,40,0.7); animation-name: atsgParticle; animation-timing-function: ease-in-out; animation-iteration-count: infinite; pointer-events: none; }
    .atsg-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .atsg-hero-left { flex: 0 0 46%; }
    .atsg-hero-left h1 { margin: 0 0 20px; font-size: 48px; line-height: 1.1; font-weight: 900; letter-spacing: -1.6px; color: white; }
    .atsg-hero-left p { margin: 0 0 32px; font-size: 17px; line-height: 1.7; color: #94A3B8; max-width: 460px; }
    .atsg-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .atsg-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; min-height: 420px; }

    .atsg-dash-card { width: 420px; background: rgba(255,255,255,0.96); backdrop-filter: blur(18px); border-radius: 20px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); overflow: hidden; animation: atsgHeroFloat 6.5s ease-in-out infinite; position: relative; z-index: 2; }
    .atsg-dash-chrome { background: #F1F5F9; padding: 11px 14px; display: flex; align-items: center; gap: 7px; border-bottom: 1px solid #E2E8F0; }
    .atsg-dash-chrome span { width: 10px; height: 10px; border-radius: 50%; background: #CBD5E1; display: inline-block; }
    .atsg-dash-chrome span:nth-child(1) { background: #FC5F5A; }
    .atsg-dash-chrome span:nth-child(2) { background: #FDBA2F; }
    .atsg-dash-chrome span:nth-child(3) { background: #28C840; }
    .atsg-dash-url { flex: 1; background: white; border-radius: 6px; padding: 4px 10px; margin-left: 6px; font-size: 10px; color: #94A3B8; display: flex; align-items: center; gap: 5px; border: 1px solid #E2E8F0; }
    .atsg-dash-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
    .atsg-dash-row { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #F1F5F9; border-radius: 12px; background: #FAFBFF; }
    .atsg-dash-row-icon { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .atsg-dash-row-text { flex: 1; min-width: 0; }
    .atsg-dash-row-label { font-size: 12.5px; font-weight: 800; color: #111827; }
    .atsg-dash-row-sub { font-size: 10.5px; color: #94A3B8; margin-top: 1px; }
    .atsg-dash-row-bar { margin-top: 6px; height: 4px; border-radius: 2px; background: #E2E8F0; overflow: hidden; }
    .atsg-dash-row-bar-fill { height: 100%; background: linear-gradient(90deg,#34D399,#059669); border-radius: 2px; }
    .atsg-dash-row-value { font-size: 11.5px; font-weight: 800; padding: 4px 9px; border-radius: 7px; flex-shrink: 0; }

    .atsg-float-badge { position: absolute; z-index: 5; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.92); backdrop-filter: blur(12px); border-radius: 12px; padding: 9px 14px 9px 9px; box-shadow: 0 16px 36px rgba(0,0,0,0.35); font-size: 12.5px; font-weight: 800; color: #111827; white-space: nowrap; animation: atsgHeroFloat 5.5s ease-in-out infinite; }
    .atsg-float-badge-1 { top: 6%; right: 2%; animation-delay: 0.2s; }
    .atsg-float-badge-2 { bottom: 8%; left: 0%; animation-delay: 0.6s; }
    .atsg-float-icon { width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .atsg-float-icon-green { background: linear-gradient(135deg,#D1FAE5,#A7F3D0); color: #059669; }
    .atsg-float-icon-red { background: linear-gradient(135deg,#FFE4E6,#FECDD3); color: #EF1D28; }

    /* Pipeline */
    .atsg-pipe-scroll { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -48px; padding: 4px 48px 16px; }
    .atsg-pipe-scroll::-webkit-scrollbar { display: none; }
    .atsg-pipe-track { display: flex; align-items: stretch; gap: 0; width: max-content; }
    .atsg-pipe-card { position: relative; width: 250px; background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 24px 20px; box-shadow: 0 4px 24px rgba(15,23,42,0.05); transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .atsg-pipe-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .atsg-pipe-num { position: absolute; top: 20px; right: 20px; font-size: 11px; font-weight: 800; color: #E2E8F0; }
    .atsg-pipe-icon { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; box-shadow: 0 6px 16px rgba(239,29,40,0.28); }
    .atsg-pipe-title { font-size: 15.5px; font-weight: 800; color: #0F172A; margin-bottom: 6px; }
    .atsg-pipe-tag { margin: 0; color: #64748B; font-size: 12.5px; line-height: 1.55; }
    .atsg-pipe-body { max-height: 0; overflow: hidden; transition: max-height 0.35s ease, margin-top 0.35s ease; margin-top: 0; font-size: 12px; color: #374151; line-height: 1.6; border-top: 1px solid transparent; }
    .atsg-pipe-card:hover .atsg-pipe-body { max-height: 160px; margin-top: 12px; border-top-color: #F1F5F9; padding-top: 10px; }
    .atsg-pipe-arrow { width: 36px; display: flex; align-items: center; justify-content: center; position: relative; flex-shrink: 0; }
    .atsg-pipe-arrow-line { width: 100%; height: 2px; background: linear-gradient(90deg,#FED7D7,#EF1D28); border-radius: 2px; }
    .atsg-pipe-arrow-icon { position: absolute; color: #EF1D28; background: white; border-radius: 50%; padding: 2px; }

    /* Scanner demo */
    .atsg-scanner-card { max-width: 720px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 24px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); padding: 40px; }
    .atsg-scanner-drop { text-align: center; }
    .atsg-scanner-drop-icon { width: 60px; height: 60px; border-radius: 16px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .atsg-scanner-drop-title { font-size: 15px; font-weight: 700; color: #0F172A; margin-bottom: 20px; }
    .atsg-scanner-steps { display: flex; flex-direction: column; gap: 8px; margin-top: 24px; max-width: 300px; margin-left: auto; margin-right: auto; text-align: left; }
    .atsg-scanner-step { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #94A3B8; font-weight: 600; }
    .atsg-scanner-step.done { color: #059669; }
    .atsg-scanner-step svg { color: #059669; flex-shrink: 0; }
    .atsg-scanner-step-dot { width: 14px; height: 14px; border-radius: 50%; border: 2px solid #E2E8F0; flex-shrink: 0; }
    .atsg-scanner-result-top { display: flex; align-items: center; gap: 24px; margin-bottom: 28px; }
    .atsg-scanner-result-title { font-size: 17px; font-weight: 800; color: #0F172A; margin-bottom: 6px; }
    .atsg-scanner-result-top p { margin: 0 0 4px; color: #64748B; font-size: 13px; line-height: 1.6; }
    .atsg-scanner-result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; padding-top: 24px; border-top: 1px solid #F1F5F9; }
    .atsg-scanner-result-label { font-size: 11px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 10px; }
    .atsg-scanner-chips { display: flex; flex-wrap: wrap; gap: 7px; }
    .atsg-scanner-chip { display: inline-flex; align-items: center; gap: 4px; background: #F1F5F9; color: #94A3B8; font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 999px; }
    .atsg-scanner-chip.matched { background: #DCFCE7; color: #059669; }
    .atsg-scanner-checks { display: flex; flex-direction: column; gap: 6px; }
    .atsg-scanner-check { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: #374151; font-weight: 600; }
    .atsg-scanner-check svg { color: #059669; flex-shrink: 0; }

    /* Keyword matching / tailor card (shared with ATS scanner styling) */
    .atsg-tailor-card { max-width: 1080px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 24px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); padding: 32px; }
    .atsg-tailor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
    .atsg-tailor-col-head { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 12px; }
    .atsg-tailor-text { background: #FAFBFF; border: 1px solid #F1F5F9; border-radius: 14px; padding: 18px; }
    .atsg-tailor-text p { margin: 0; font-size: 13.5px; color: #374151; line-height: 1.8; }
    .atsg-kw { background: #F1F5F9; color: #64748B; border-radius: 5px; padding: 1px 4px; font-weight: 700; transition: all 0.3s ease; }
    .atsg-kw.active { background: #DCFCE7; color: #059669; }
    .atsg-tailor-footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; padding-top: 20px; border-top: 1px solid #F1F5F9; }
    .atsg-tailor-score span:first-child { display: block; font-size: 11px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px; }
    .atsg-tailor-score-row { display: flex; align-items: baseline; gap: 10px; }
    .atsg-tailor-score-value { font-size: 30px; font-weight: 900; letter-spacing: -1px; }
    .atsg-tailor-score-sub { font-size: 12px; color: #94A3B8; font-weight: 600; }

    /* Do / Don't */
    .atsg-dd-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; max-width: 1000px; margin: 0 auto; }
    .atsg-dd-col-head { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 800; margin-bottom: 18px; }
    .atsg-dd-col-head.positive { color: #166534; }
    .atsg-dd-col-head.negative { color: #9F1239; }
    .atsg-dd-card { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; padding: 18px 20px; margin-bottom: 12px; }
    .atsg-dd-card.positive:hover { border-color: #BBF7D0; }
    .atsg-dd-card.negative:hover { border-color: #FECDD3; }
    .atsg-dd-head { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 700; color: #0F172A; margin-bottom: 10px; }
    .atsg-dd-icon { width: 20px; height: 20px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
    .atsg-dd-card.positive .atsg-dd-icon { background: #059669; }
    .atsg-dd-card.negative .atsg-dd-icon { background: #EF1D28; }
    .atsg-dd-snippet { font-family: 'SFMono-Regular', Consolas, monospace; font-size: 12px; padding: 8px 12px; border-radius: 8px; margin-bottom: 0; }
    .atsg-dd-card.positive .atsg-dd-snippet { background: #F0FDF4; color: #166534; }
    .atsg-dd-card.negative .atsg-dd-snippet { background: #FFF1F2; color: #9F1239; }
    .atsg-dd-explain { max-height: 0; overflow: hidden; transition: max-height 0.35s ease, margin-top 0.35s ease; margin-top: 0; font-size: 12px; color: #64748B; line-height: 1.6; }
    .atsg-dd-card:hover .atsg-dd-explain { max-height: 100px; margin-top: 10px; }

    /* Before / After */
    .atsg-ba-card { max-width: 900px; margin: 0 auto; }
    .atsg-ba-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    .atsg-ba-resume { background: white; border: 1.5px solid #E5E7EB; border-radius: 20px; padding: 24px; }
    .atsg-ba-resume.after { border-color: #BBF7D0; }
    .atsg-ba-resume-label { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; color: #94A3B8; margin-bottom: 14px; }
    .atsg-ba-mock { background: #FAFBFF; border: 1px solid #F1F5F9; border-radius: 12px; padding: 16px; margin-bottom: 16px; min-height: 140px; }
    .atsg-ba-mock-cols { display: flex; gap: 10px; margin-bottom: 12px; }
    .atsg-ba-mock-col { flex: 1; display: flex; flex-direction: column; gap: 5px; }
    .atsg-ba-mock-col span, .atsg-ba-mock-single span { display: block; height: 5px; border-radius: 3px; background: #E2E8F0; width: 100%; }
    .atsg-ba-mock-col span.short, .atsg-ba-mock-single span.short { width: 55%; }
    .atsg-ba-mock-single { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
    .atsg-ba-mock-flag { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: #EF1D28; margin-top: 6px; }
    .atsg-ba-mock-flag.good { color: #059669; }
    .atsg-ba-score-bar { height: 8px; border-radius: 4px; margin-bottom: 8px; }
    .atsg-ba-score-label { font-size: 13px; font-weight: 800; }

    /* Checklist */
    .atsg-checklist-card { max-width: 700px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 30px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); }
    .atsg-checklist-progress-row { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
    .atsg-checklist-progress-track { flex: 1; height: 8px; border-radius: 4px; background: #F1F5F9; overflow: hidden; }
    .atsg-checklist-progress-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg,#34D399,#059669); }
    .atsg-checklist-progress-label { font-size: 13px; font-weight: 800; color: #374151; flex-shrink: 0; }
    .atsg-checklist-badge { display: flex; align-items: center; gap: 8px; background: linear-gradient(135deg,#059669,#10B981); color: white; font-size: 13px; font-weight: 800; padding: 10px 16px; border-radius: 12px; margin-bottom: 16px; }
    .atsg-checklist-items { display: flex; flex-direction: column; gap: 2px; }
    .atsg-checklist-item { display: flex; align-items: flex-start; gap: 12px; text-align: left; border: none; background: none; cursor: pointer; font-family: inherit; padding: 11px 8px; border-radius: 10px; font-size: 13.5px; color: #374151; transition: background 0.15s; }
    .atsg-checklist-item:hover { background: #FAFBFF; }
    .atsg-checklist-item.checked { color: #94A3B8; text-decoration: line-through; }
    .atsg-checklist-box { width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid #D1D5DB; flex-shrink: 0; margin-top: 1px; display: flex; align-items: center; justify-content: center; color: white; transition: all 0.15s; }
    .atsg-checklist-item.checked .atsg-checklist-box { background: #059669; border-color: #059669; }

    /* Myths flip cards */
    .atsg-myths-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .atsg-flip { perspective: 1200px; height: 190px; }
    .atsg-flip-inner { position: relative; width: 100%; height: 100%; transition: transform 0.6s cubic-bezier(0.4,0.2,0.2,1); transform-style: preserve-3d; }
    .atsg-flip:hover .atsg-flip-inner { transform: rotateY(180deg); }
    .atsg-flip-face { position: absolute; inset: 0; backface-visibility: hidden; border-radius: 20px; padding: 24px; display: flex; flex-direction: column; justify-content: center; box-shadow: 0 4px 24px rgba(15,23,42,0.06); }
    .atsg-flip-front { background: white; border: 1.5px solid #FECDD3; }
    .atsg-flip-back { background: linear-gradient(135deg,#0F172A,#1E293B); color: white; transform: rotateY(180deg); }
    .atsg-flip-label { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 10px; }
    .atsg-flip-front .atsg-flip-label { color: #EF1D28; }
    .atsg-flip-back .atsg-flip-label { color: #34D399; }
    .atsg-flip-face p { margin: 0; font-size: 14px; line-height: 1.55; font-weight: 600; }
    .atsg-flip-front p { color: #0F172A; }
    .atsg-flip-back p { color: #E2E8F0; font-weight: 500; }

    /* Stats */
    .atsg-stats-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 20px; }
    .atsg-stat-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 30px 20px; text-align: center; box-shadow: 0 4px 20px rgba(15,23,42,0.05); transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .atsg-stat-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .atsg-stat-icon { width: 48px; height: 48px; border-radius: 14px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .atsg-stat-value { font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -1px; margin-bottom: 8px; }
    .atsg-stat-label { font-size: 12.5px; color: #64748B; font-weight: 600; line-height: 1.5; }

    /* FAQ */
    .atsg-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .atsg-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .atsg-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .atsg-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .atsg-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .atsg-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .atsg-faq-item.open .atsg-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .atsg-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .atsg-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .atsg-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .atsg-faq-item.open .atsg-faq-icon .bar { background: #EF1D28; }
    .atsg-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Final CTA */
    .atsg-cta-outer { padding: 20px 48px 96px; }
    .atsg-cta-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 64px 56px; border: 1px solid #1A2A3E; }
    .atsg-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .atsg-cta-glow { position: absolute; top: -100px; right: -80px; width: 500px; height: 400px; background: radial-gradient(ellipse, rgba(239,29,40,0.18) 0%, transparent 65%); pointer-events: none; }
    .atsg-cta-content { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 48px; flex-wrap: wrap; }
    .atsg-cta-left { flex: 0 0 auto; max-width: 460px; }
    .atsg-cta-left h2 { margin: 0 0 12px; font-size: 36px; font-weight: 900; color: white; letter-spacing: -0.8px; line-height: 1.15; }
    .atsg-cta-left p { margin: 0 0 26px; color: #94A3B8; font-size: 15px; line-height: 1.6; }
    .atsg-cta-right { flex: 1; min-width: 260px; display: flex; justify-content: center; }
    .atsg-cta-dash { width: 280px; background: white; border-radius: 16px; box-shadow: 0 40px 90px rgba(0,0,0,0.5); overflow: hidden; }
    .atsg-cta-dash-chrome { background: #F1F5F9; padding: 9px 12px; display: flex; gap: 5px; }
    .atsg-cta-dash-chrome span { width: 8px; height: 8px; border-radius: 50%; background: #CBD5E1; display: inline-block; }
    .atsg-cta-dash-body { padding: 14px; display: flex; flex-direction: column; gap: 8px; }
    .atsg-cta-dash-row { display: flex; align-items: center; gap: 10px; padding: 8px; border: 1px solid #F1F5F9; border-radius: 10px; background: #FAFBFF; }
    .atsg-cta-dash-row-icon { width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .atsg-cta-dash-row-label { flex: 1; font-size: 11px; font-weight: 800; color: #111827; }
    .atsg-cta-dash-row-value { font-size: 11px; font-weight: 800; flex-shrink: 0; }

    /* Responsive */
    @media (max-width: 1100px) {
      .atsg-stats-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .atsg-myths-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 900px) {
      .atsg-hero { padding: 64px 24px 80px; }
      .atsg-hero-inner { flex-direction: column; gap: 48px; text-align: center; }
      .atsg-hero-left { flex: none; }
      .atsg-hero-left p { margin-left: auto; margin-right: auto; }
      .atsg-hero-ctas { justify-content: center; }
      .atsg-hero-left h1 { font-size: 38px; }
      .atsg-section { padding: 56px 24px; }
      .atsg-section-tint { padding-left: 24px; padding-right: 24px; }
      .atsg-pipe-track { flex-direction: column; align-items: center; width: 100%; max-width: 340px; margin: 0 auto; }
      .atsg-pipe-card { width: 100%; }
      .atsg-pipe-arrow { width: auto; min-height: 44px; }
      .atsg-pipe-arrow-line { width: 2px; height: 28px; background: linear-gradient(180deg,#FED7D7,#EF1D28); }
      .atsg-pipe-arrow-icon { transform: rotate(90deg); }
      .atsg-pipe-body { max-height: none !important; margin-top: 12px !important; border-top-color: #F1F5F9 !important; padding-top: 10px !important; }
      .atsg-tailor-grid { grid-template-columns: 1fr; }
      .atsg-dd-grid { grid-template-columns: 1fr; gap: 40px; }
      .atsg-dd-explain { max-height: none !important; margin-top: 10px !important; }
      .atsg-ba-grid { grid-template-columns: 1fr; }
      .atsg-cta-content { flex-direction: column; text-align: center; }
      .atsg-cta-left { text-align: center; max-width: none; }
    }
    @media (max-width: 640px) {
      .atsg-stats-grid { grid-template-columns: 1fr 1fr; }
      .atsg-myths-grid { grid-template-columns: 1fr; }
      .atsg-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .atsg-hero-right { display: none; }
      .atsg-cta-card { padding: 44px 24px; }
      .atsg-cta-left h2 { font-size: 26px; }
      .atsg-scanner-card { padding: 24px; }
      .atsg-scanner-result-grid { grid-template-columns: 1fr; }
      .atsg-checklist-card { padding: 22px; }
    }
  `}</style>
);
